import { Flexbox } from '@luminovo/design-system';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { BarStackHorizontal } from '@visx/shape';
import React from 'react';

import { getDefaultKeyColor } from '../../color/getDefaultColor';
import { Legend } from './components/Legend';
import { StackedBar } from './components/StackedBar';
import { BarContainers } from './components/StackedBarContainer';
import { Datum, SingleHorizontalStackedBarChartProps } from './types';
import { clearUnselectedKeys } from './util/clearUnselectedKeys';

export function SingleHorizontalStackedBarChart<TKeys extends string, T extends Datum<TKeys>>(
    props: SingleHorizontalStackedBarChartProps<TKeys, T>,
): JSX.Element {
    const {
        width,
        formatKey,
        keys,
        data: inputData,
        getTooltip,
        getColor = getDefaultKeyColor(keys),
        getKeyCount,
    } = props;

    const [selectedKeys] = React.useState(keys);
    const data = clearUnselectedKeys(inputData, keys, selectedKeys);

    const barHeight = 24;
    const height = data.length * barHeight;
    // bounds
    const xMax = width;
    const yMax = height;

    // scales
    const yScale = scaleBand({
        domain: data.map((d) => d.label),
        range: [0, yMax],
        padding: 0.1,
    });

    const domainMax = Math.max(
        ...data.map((d) => {
            return selectedKeys.reduce((max, key) => max + (d[key] ?? 0), 0);
        }),
    );
    const xScale = scaleLinear<number>({
        domain: [0, domainMax],
        range: [0, xMax],
    });

    const colorScale = scaleOrdinal<string, string>({
        domain: keys,
        range: keys.map(getColor),
    });

    return (
        <Flexbox
            gap="4px"
            sx={{
                flexDirection: 'column',
                width: width,
                height: '100%',
            }}
        >
            <svg width={width} height={height} style={{ overflow: 'visible' }}>
                <BarContainers barHeight={barHeight} data={data} xScale={xScale} yScale={yScale} />
                <Group top={0} left={0}>
                    <BarStackHorizontal<T, TKeys>
                        data={data}
                        keys={keys}
                        height={height}
                        y={(d) => d.label}
                        yScale={yScale}
                        xScale={xScale}
                        color={colorScale}
                    >
                        {(barStacks) =>
                            barStacks.map((barStack) => {
                                return barStack.bars.map((bar) => {
                                    return (
                                        <StackedBar
                                            key={`${barStack.index}-${bar.index}-${bar.key}`}
                                            bar={bar}
                                            barStack={barStack}
                                            {...props}
                                        />
                                    );
                                });
                            })
                        }
                    </BarStackHorizontal>
                </Group>
            </svg>
            {keys.length > 1 && (
                <Legend
                    keys={keys}
                    formatKey={formatKey}
                    getColor={getColor}
                    getTooltip={getTooltip}
                    getKeyCount={getKeyCount}
                />
            )}
        </Flexbox>
    );
}
